// src/contexts/SidebarContext.tsx
import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';

interface SidebarContextType {
  isSidebarOpen: boolean;
  toggleSidebar: () => void;
  syncRoute: () => void;
}

const SidebarContext = createContext<SidebarContextType | undefined>(undefined);

export const useSidebar = () => {
  const context = useContext(SidebarContext);
  if (!context) {
    throw new Error('useSidebar must be used within a SidebarProvider');
  }
  return context;
};

const getSidebarStorageKey = () => {
  try {
    const params = new URLSearchParams(window.location.search);
    const view = params.get('view');
    const page = params.get('page');
    if (view === 'image' || view === 'song' || (page === 'article' && view === 'detail')) {
      return 'detailSidebarOpen';
    }
  } catch {
    // Ignore URL parsing errors.
  }
  return 'sidebarOpen';
};

const getInitialSidebarState = () => {
  try {
    const key = getSidebarStorageKey();
    const saved = localStorage.getItem(key);
    if (saved === null) return key !== 'detailSidebarOpen';
    return saved === 'true';
  } catch {
    return true;
  }
};

export const SidebarProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [desktopOpen, setDesktopOpen] = useState(getInitialSidebarState);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobile, setMobile] = useState(window.innerWidth < 768);
  const syncRoute = useCallback(() => {
    setMobileOpen(false);
    setDesktopOpen(getSidebarStorageKey() === 'detailSidebarOpen' ? false : getInitialSidebarState());
  }, []);
  useEffect(() => {
    const resize = () => { setMobile(window.innerWidth < 768); setMobileOpen(false); };
    window.addEventListener('resize', resize);
    return () => window.removeEventListener('resize', resize);
  }, []);
  const isSidebarOpen = mobile ? mobileOpen : desktopOpen;

  const toggleSidebar = () => {
    if (mobile) { setMobileOpen(prev => !prev); return; }
    setDesktopOpen(prev => {
      const next = !prev;
      try {
        const key = getSidebarStorageKey();
        localStorage.setItem(key, String(next));
      } catch {
        // Ignore storage errors.
      }
      return next;
    });
  };

  return (
    <SidebarContext.Provider value={{ isSidebarOpen, toggleSidebar, syncRoute }}>
      {children}
    </SidebarContext.Provider>
  );
};
